import React, { useState, useEffect, Suspense, lazy, useRef } from "react";
import { Typography, Snackbar, CircularProgress, LinearProgress, AppBar, Toolbar, useScrollTrigger, Box, styled, useTheme } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useSpring, animated, config as springConfig } from "react-spring"; // Updated to hooks API
import { useNavigate, useLocation } from "react-router-dom";

import { fetchUser } from "../../actions/user_actions";
import {
  resetCoins,
  resetMaxCoins,
  resetCoinDefault,
  closePurchaseDialogue
} from "../../actions/reward_actions";

import { Routes, Route } from "react-router-dom";
import Navigation from "./Navigaton";
import * as ROUTES from "../../constants/routes";
import Demographics from "./Demographics";
import Hello from "./Hello";
import WellDone from "./WellDone";
import PurchaseComplete from "./PurchaseComplete";
import { ReactComponent as SmallCoin } from "../Common/Icons/smallCoin.svg";
import { ReactComponent as RewardShield } from "../Profile/icons/rewardShield.svg";

import { Alert, AlertTitle } from "@mui/material"; // Corrected Alert import

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}


const Profile = lazy(() => import("../Profile"));
const ProfileSettings = lazy(() => import("../Profile/ProfileSettings"));
const Default = lazy(() => import("./Default"));
const Jobs = lazy(() => import("../Jobs"));
const Chat = lazy(() => import("../Chat"));
// const JobDetails = lazy(() => import("../Jobs/JobDetails"));
const Missions = lazy(() => import("../Missions"));
const ResourceDetails = lazy(() => import("../Resources/ResourceDetails"));
const Privacy = lazy(() => import("../Privacy"));
const Faq = lazy(() => import("../Faq"));
const OrgProfile = lazy(() => import("../OrgProfile"));
const ApplicationAssistant = lazy(() => import("../ApplicationAssistant"));

/**
 * Creates a relative path for nested routes from an absolute path constant.
 * e.g., turns "/app/jobs" into "jobs"
 * @param {string} absolutePath - The absolute path from ROUTES constants.
 * @returns {string} The path relative to the "/app" base.
 */
const relativePath = (absolutePath) => absolutePath.replace(ROUTES.APP + '/', '');

const Main = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [chatActive, setChatActive] = useState(false);
  const [isFocused, setIsFocused] = useState(true);
  const [shouldShowCoins, setShouldShowCoins] = useState(false);
  const [showCoins, setShowCoins] = useState(false);
  const [isLevelUp, setIsLevelUp] = useState(false);

  const user = useSelector(state => state.user);
  const rewards = useSelector(state => state.rewards);
  const { newCoins = 0, level = 1, dailyLimitReachedNotification, purchaseComplete, stripeOrders, showOrgDefault } = rewards;
  const { email, orgDetails = {}, showHello } = user;
  const prevUser = usePrevious(user);
  const prevRewards = usePrevious(rewards);
  const { name } = orgDetails;
  const removedCoins = newCoins < 0 ? Math.abs(newCoins) : false;

  const handleFocus = () => {
    setTimeout(() => {
      setIsFocused(true);
      setShowCoins(shouldShowCoins);
    }, 300);
  };
  const handleBlur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    const root = document.querySelector(":root");
    if (root) {
      root.style.setProperty("--vh", window.innerHeight / 100 + "px");
    }

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    dispatch(fetchUser());
    
    window.addEventListener("resize", () => {
      if (root) {
        root.style.setProperty("--vh", window.innerHeight / 100 + "px");
      }
    });

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith(ROUTES.APP) && location.pathname !== ROUTES.APP) {
      localStorage.setItem("lastpath", location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    const lastPath = localStorage.getItem("lastpath");
    if (lastPath && lastPath !== location.pathname && lastPath.startsWith(ROUTES.APP) && lastPath !== ROUTES.APP) {
      navigate(lastPath, { replace: true });
    }
  }, []);

  useEffect(() => {
  if (!user || !prevUser) {
    return;
  }

  if (user.showChat !== prevUser.showChat) {
    if (user.showChat === true) {
      setChatActive(true);
      if (!location.pathname.includes(ROUTES.CHAT)) {
        navigate(ROUTES.CHAT);
      }
    } else {
      setChatActive(false);
      if (location.pathname.includes(ROUTES.CHAT)) {
        navigate(ROUTES.APP);
      }
    }
  }
}, [user, navigate, location.pathname, ROUTES]);

  // Handles coin state changes
  useEffect(() => {
    // Only proceed if prevRewards is available (i.e., not the very first render)
    // This ensures we only react to *changes* after initial setup.
    if (prevRewards) {
      // Check if newCoins has changed
      if (rewards.newCoins !== prevRewards.newCoins) {
        if (rewards.newCoins !== 0) {
          setShouldShowCoins(true); // Keep track that we should show coins
          setShowCoins(isFocused); // Only show if the window is focused
        } else { // newCoins became 0
          setShowCoins(false);
          setShouldShowCoins(false);
          setIsLevelUp(false);
        }
      }
      // Check if level has changed since the last render
      if (rewards.level !== prevRewards.level && rewards.level >= 1) {
        setIsLevelUp(true);
      }
    }
  }, [rewards, isFocused, prevRewards]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(resetCoins());
  };

  // All react-spring animations converted to the useSpring hook
  const animatedRewardShieldProps = useSpring({
    from: { transform: "scale(-1, 1)" },
    to: { transform: "scale(1, 1)" },
    config: springConfig.wobbly,
    delay: 1200,
  });

  const animatedSmallCoinProps = useSpring({
    from: { transform: "scale(-1, 1)" },
    to: { transform: "scale(1, 1)" },
    config: springConfig.wobbly,
    delay: 1200,
  });

  const AnimatedSmallCoin = animated(SmallCoin);
  const AnimatedRewardShield = animated(RewardShield);

  return (
    <Box sx={{ flexGrow: 1 }}>
      {!email && (
        <Box sx={{ display: "flex", width: "100%" }}>
          <LinearProgress sx={{ width: "100%" }} />
        </Box>
      )}
      {email && (
        <>
          {showHello && <Hello />}
          <Demographics />
          <WellDone />
          <Navigation />
          <Box sx={{ marginTop: { xs: 8, md: 10 }, marginBottom: 2 }}>
            <Suspense
              fallback={
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <CircularProgress size={28} />
                </Box>
              }
            >
              <Routes>
                {/* Use `index` for the default route at /app */}
                <Route index element={chatActive ? <Default /> : <Jobs />} />

                {/* Use the helper to create relative paths from your constants */}
                <Route path={relativePath(ROUTES.JOBS)} element={<Jobs />} />
                {/* <Route path={relativePath(ROUTES.JOB_DETAILS)} element={<JobDetails />} /> */}
                <Route path={relativePath(ROUTES.CHAT)} element={<Chat />} />
                <Route path={relativePath(ROUTES.RESOURCE_DETAILS)} element={<ResourceDetails />} />
                <Route path={relativePath(ROUTES.PROFILE)} element={<Profile />} />
                <Route path={relativePath(ROUTES.PROFILE_SETTINGS)} element={<ProfileSettings />} />
                <Route path={relativePath(ROUTES.ORGANISATION_PROFILE)} element={<OrgProfile />} />
                <Route path={relativePath(ROUTES.MISSIONS)} element={<Missions />} />
                <Route path={relativePath(ROUTES.PRIVACY)} element={<Privacy />} />
                <Route path={relativePath(ROUTES.FAQ)} element={<Faq />} />
                <Route path={relativePath(ROUTES.APPLICATION_ASSISTANT)} element={<ApplicationAssistant />} />

                {/* Catch-all for any paths under /app that don't match */}
                <Route path="*" element={<Default />} />
              </Routes>
            </Suspense>
          </Box>
          <Snackbar
            open={removedCoins && showCoins && !purchaseComplete}
            onClose={handleClose}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success" onClose={handleClose} sx={{ backgroundColor: "#53d28e" }}>
              {"Spent " + removedCoins + " coins."}
            </Alert>
          </Snackbar>
          <Snackbar
            open={showCoins && isLevelUp && !removedCoins && !purchaseComplete}
            onClose={handleClose}
            autoHideDuration={5000}
            key={"levelup"}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              severity="warning"
              icon={
                <Typography variant="body1" sx={{ fontSize: 20, fontWeight: "bold", color: "#FFFFFF" }}>
                  <AnimatedRewardShield style={animatedRewardShieldProps} sx={{ ml: 1, mr: 1, width: 48, height: 48, mb: -0.75 }} />
                </Typography>
              }
              sx={{ backgroundColor: "#FF6A14", color: "#FFFFFF" }}
            >
              <AlertTitle sx={{ fontSize: 16 }}><strong>Level Up! </strong></AlertTitle>
              +{newCoins} points. You are on Level {level} 🏆
            </Alert>
          </Snackbar>
          <Snackbar
            open={showCoins && shouldShowCoins && !isLevelUp && !removedCoins && !purchaseComplete}
            onClose={handleClose}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              severity="success"
              icon={
                <Typography variant="body1" sx={{ fontSize: 20, fontWeight: "bold", textAlign: "center", color: "#FFFFFF" }}>
                  <AnimatedSmallCoin style={animatedSmallCoinProps} sx={{ ml: 1, mr: 1, width: 24, height: 24, mb: -0.75 }} />
                  <Box component="span" sx={{ fontSize: 20, fontWeight: "bold", color: "#FFFFFF" }}>
                    +{parseInt(newCoins)}
                  </Box>
                </Typography>
              }
              sx={{ backgroundColor: "#53d28e", color: "#FFFFFF" }}
            >
              You just earned {newCoins} points. Keep it up!
            </Alert>
          </Snackbar>
          <Snackbar
            open={dailyLimitReachedNotification && !purchaseComplete}
            onClose={() => dispatch(resetMaxCoins())}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="error">
              Maximum number of coins earned today. Earn more tomorrow
            </Alert>
          </Snackbar>
          <Snackbar
            open={showOrgDefault}
            onClose={() => dispatch(resetCoinDefault())}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              severity="success"
              icon={
                <Typography variant="body1" sx={{ fontSize: 20, fontWeight: "bold", color: "#FFFFFF" }}>
                  <AnimatedSmallCoin style={animatedSmallCoinProps} sx={{ ml: 1, mr: 1, width: 24, height: 24, mb: -0.75 }} />
                </Typography>
              }
              sx={{ backgroundColor: "#53d28e", color: "#FFFFFF" }}
            >
              {"+ " + showOrgDefault + " coins added from " + name}
            </Alert>
          </Snackbar>
          <PurchaseComplete open={purchaseComplete} stripeOrders={stripeOrders} onClose={() => dispatch(closePurchaseDialogue())} />
        </>
      )}
    </Box>
  );
};

export default Main;